<template>
  <ion-content>
    <ion-card>
      <ion-card-header>
        <ion-text>
          <h1>Bridges</h1>
        </ion-text>
      </ion-card-header>

      <ion-card-content>
        <ion-row>
          <ion-list-header>
            <ion-label>Latest releases</ion-label>
          </ion-list-header>
          <ion-col
            v-for="(release, track) in releaseTracks"
            :key="track"
            size="3"
          >
            <ion-item>
              <ion-label>{{ track }}: <strong v-if="release">{{ release.version }}</strong></ion-label>
            </ion-item>
          </ion-col>
          <ion-col
            size="3"
          >
            <ion-item>
              <ion-label>Hide inactive and offline:</ion-label>
              <ion-toggle
                :checked="hideInactive"
                @ionChange="hideInactive = !hideInactive"
              />
            </ion-item>
          </ion-col>
        </ion-row>


        <ion-item class="search">
          <ion-label position="floating">
            Search
          </ion-label>
          <ion-input-vue
            v-model="search"
            @ionChange="flushUpdateQueue"
          />
        </ion-item>

        <v-data-table
          class="bridge-data-table"
          :search="search"
          :headers="headers"
          :items="items"
          :items-per-page="30"
          :loading="busy"
          :options.sync="options"
          :footer-props="{'items-per-page-options':[15, 30, 50, 100, -1]}"
          @click:row="onBridgeClicked"
          @pagination="flushUpdateQueue"
          @update:sort-by="flushUpdateQueue"
          @update:sort-desc="flushUpdateQueue"
          @page-count="pageCount = $event"
        >
          <template v-slot:item.serial_number="{ item }">
            {{ item.serial_number }}
            <ion-icon
              v-if="item.is_virtual"
              class="inventory-badge"
              :src="i.cloudOutline"
            />
          </template>
          <template v-slot:item.cachedBridge.is_online="{ item }">
            <ion-badge
              v-if="!item.is_active"
              class="inactive hydrated"
            >
              Inactive
            </ion-badge>
            <template v-else>
              <ion-badge
                v-if="isOnline(item)"
                class="online hydrated"
              >
                Online
              </ion-badge>
              <ion-badge
                v-else
                class="offline hydrated"
              >
                Offline
              </ion-badge>
              <template v-if="item.cachedBridge">
                <template v-if="isOnline(item)">
                  (for {{ item.cachedBridge.last_connectivity_event | period }})
                </template>
                <template v-else>
                  (since {{ item.cachedBridge.last_connectivity_event | period }})
                </template>
              </template>
            </template>
          </template>

          <template v-slot:item.lastSeenInCloud="{ item }">
            <ion-spinner
              v-if="statusNeedsLoading(item)"
            />
            <ion-badge
              v-else-if="item.lastSeenInCloud === null"
              class="inactive hydrated"
            >
              Never
            </ion-badge>
            <ion-badge
              v-else-if="item.lastSeenInCloud === -1"
              class="error hydrated"
            >
              <ion-icon :src="i.warning" />
            </ion-badge>
            <ion-badge
              v-else-if="isRecentlySeen(item)"
              class="online hydrated"
            >
              {{ item.lastSeenInCloud | period }} ago
            </ion-badge>
            <ion-badge
              v-else
              class="offline hydrated"
            >
              {{ item.lastSeenInCloud | period }} ago
            </ion-badge>
          </template>

          <template v-slot:item.balena_id="{ value }">
            <code :title="value">{{ value.substring(0, 7) }}</code>

            <ion-button
              v-clipboard:copy="value"
              class="ion-margin-start uuid-clipboard"
              fill="outline"
              size="small"
              @click.stop
            >
              <ion-icon
                :src="i.clipboardOutline"
                mode="ios"
              />
            </ion-button>
            <ion-button
              :href="`https://dashboard.balena-cloud.com/devices/${value}`"
              target="_blank"
              color="primary"
              class="uuid-clipboard"
              fill="outline"
              size="small"
              @click.stop
            >
              <ion-icon
                :src="i.openOutline"
                mode="ios"
              />
            </ion-button>
          </template>

          <template v-slot:item.osVersion="{ item }">
            <template v-if="item.cachedBridge">
              {{ item.cachedBridge.os_version }} / {{ item.cachedBridge.os_variant }}
            </template>
            <template v-else>
              -
            </template>
          </template>

          <template v-slot:item.cachedBridge.ip_address="{ value }">
            {{ value | optional }}
          </template>

          <template v-slot:item.commit.version="{ value }">
            {{ value | optional }}
          </template>
        </v-data-table>

        <v-pagination
          v-model="options.page"
          :disabled="busy"
          :length="pageCount"
        />
      </ion-card-content>
    </ion-card>
  </ion-content>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import { VDataTable } from 'vuetify/lib'
import { IonInputVue } from '@modus/ionic-vue'
import BridgeHeader from './HeaderItem'
import BridgeItem from './BridgeItem'
import userTypes from '../userTypes'

export default {
  components: {
    VDataTable,
    IonInputVue,
  },
  data () {
    return {
      headerComponent: BridgeHeader,
      itemComponent: BridgeItem,
      busy: true,
      search: '',
      options: {},
      pageCount: 0,
      hideInactive: false,
      updaterRunning: false,
      bridgesToUpdate: [],
    }
  },
  computed: {
    ...mapGetters([
      'bridges',
      'isFetchingBridges',
      'user',
    ]),
    items () {
      if (!this.hideInactive) {
        return this.bridges.bridges
      }

      return this.bridges.bridges.filter(bridge => {
        return !!bridge.is_active &&
            this.isOnline(bridge)
      })
    },
    headers () {
      if (!this.user) {
        return
      }

      const visible = [
        {
          text: 'Name',
          value: 'serial_number'
        },
        {
          text: 'Status',
          value: 'cachedBridge.is_online'
        },
        {
          text: 'Last seen',
          value: 'lastSeenInCloud'
        },
        {
          text: 'Facility name',
          value: 'facilityName'
        },
        {
          text: 'Release',
          value: 'release_track'
        },
        {
          text: 'Version',
          value: 'commit.version'
        }
      ]

      const locked = [
        {
          text: 'UUID',
          value: 'balena_id'
        },
        {
          text: 'OS Version',
          value: 'osVersion',
          sortable: false
        },
        {
          text: 'IP Address',
          value: 'cachedBridge.ip_address'
        }
      ]

      const type = this.user.type

      if(type === userTypes.SUPER_ADMIN) {
        return [
          ...visible,
          ...locked
        ]
      }
      else {
        return visible
      }
    },
    releaseTracks () {
      return this.$store.state.bridge.bridgeReleaseTracks
    }
  },
  watch: {
    bridgesToUpdate(bridges) {
      if (this.updaterRunning) return
      if (bridges.length === 0) return
      this.updateNextBridge()
    }
  },
  created() {
    if(!this.bridges.bridges.length){
      this.fetchBridges()
    }else{
      this.busy = false
    }
    this.fetchLatestReleases()
  },
  methods: {
    onBridgeClicked(bridge) {
      this.$router.push({ name: 'bridges.detail', params: { serialNumber: bridge.serial_number } })
    },
    async fetchBridges() {
      const options = this.options
      const page = options.page
      const search = this.search
      let sort = options.sortBy
      let direction = 'desc'

      if(sort && sort.length) {
        sort = sort[0]
        direction = options.sortDesc[0] ? 'desc' : 'asc'
      }
      try {
        await this.$store.dispatch('fetchBridges', { page, search, sort, direction })
      } catch (e) {
        //.. ignore
      }
      this.busy = false
    },
    statusNeedsLoading(bridge) {
      if (bridge.lastSeenInCloud !== undefined) return false
      if (this.bridgesToUpdate.find(pendingBridge => pendingBridge.serial_number === bridge.serial_number)) return true

      this.bridgesToUpdate.push(bridge)
      return true
    },
    flushUpdateQueue() {
      this.bridgesToUpdate = []
    },
    async updateNextBridge () {
      this.updaterRunning = true
      const bridge = this.bridgesToUpdate[0]
      if (!bridge) {
        this.updaterRunning = false
        return
      }
      await this.$store.dispatch('fetchBridgeStatus', bridge)
      this.bridgesToUpdate.shift()
      await new Promise(resolve => setTimeout(resolve, 500))
      this.updateNextBridge()
    },
    fetchLatestReleases() {
      this.$store.dispatch('fetchLatestBridgeReleaseTracks')
    },
    isOnline(item) {
      return item.cachedBridge && (item.cachedBridge.is_online || item.cachedBridge.is_connected_to_vpn)
    },
    isRecentlySeen(item) {
      return moment().diff(item.lastSeenInCloud, 'minutes') < 30
    }
  }
}
</script>

<style>
.bridge-data-table tr:hover {
  cursor: pointer;
}

.bridge-data-table .v-data-footer__icons-before, .bridge-data-table .v-data-footer__icons-after {
  display: none;
}

ion-toggle {
  --background-checked: var(--ion-color-primary-shade);
}
</style>
